import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  ControlDynamicComponent,
  IModalsService,
  IMODALS_SERVICE,
  INotificationsService,
  INOTIFICATIONS_SERVICE,
  P500UIBaseModal,
} from '@platform500services/p500-ui-kit';
import { Observable, forkJoin } from 'rxjs';
import { filter, pluck } from 'rxjs/operators';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { mapToBackendFormat } from '../../helpers/map-to-backend-format';
import { ActionsServiceInterface } from '../../services/actions/actions-service.interface';
import { ActionConfigInterface } from '../../services/actions/interfaces/action-config.interface';
import { ComponentModel, FieldModel } from '../../services/actions/models/field.model';
import { PageServiceInterface } from '../../services/page/page-service.interface';
import { Data, setFirstSelectValueByDefault, setFormData } from './action-form.modal.helpers';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { Router } from '@angular/router';

@Component({
  selector: 'gtd-action-form-modal',
  templateUrl: './action-form.modal.html',
})
@DestroySubscribers()
export class ActionFormModal extends P500UIBaseModal implements AfterContentInit, OnDestroy {
  @HostBinding('class') get className() {
    return `c-modal c-modal--d c-modal--action-form -primary ${this.config.getModalStyle()} ${
      this.isLoading ? 'c-modal--is--isLoading' : ''
    }`;
  }
  @Input() data: ActionFormData;
  @Input() callback = (): void => {};
  public dataResponse: Data = null;
  public isLoading = false;
  public config: ActionConfigInterface;

  public form: UntypedFormGroup;
  public components: Map<string, ComponentModel['component']> = new Map();
  public componentsSettings: Map<string, ComponentModel['settings']> = new Map();
  readonly maxFields = 6;
  public toggleField: FieldModel;
  public fields: FieldModel[];
  @ViewChildren('dynamic') private dynamicList: QueryList<ControlDynamicComponent<any>>;
  public dependHideFields: Record<string, Observable<boolean>>;
  public defaultSwitcherText = $localize`:@@common.active:Active`;

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    @Inject('ActionsServiceInterface') protected actionsService: ActionsServiceInterface,
    @Inject(INOTIFICATIONS_SERVICE) protected notificationsService: INotificationsService,
    @Inject('PageServiceInterface') protected pageService: PageServiceInterface,
    public cd: ChangeDetectorRef,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    router: Router
  ) {
    super(modalsService, router);
  }

  public originalOrder = () => 0;

  ngAfterContentInit(): void {
    this.config = this.data.config();
    this.dependHideFields = this.config.getDependHideFields();
    this.fields = this.config.getFields();
    this.toggleField = this.config.getToggleField();
    this.initForm();
    this.initSubs();
  }

  initForm(): void {
    const formGroup = {};

    this.fields.forEach((fieldModel) => {
      formGroup[fieldModel.key] = fieldModel.formControl;
      const component = fieldModel.getComponent();
      const cssClass = fieldModel.formControl.validator ? 'is-required' : '';
      const message = this.getFieldTooltip(fieldModel.key);
      this.components.set(fieldModel.key, component.component);
      this.componentsSettings.set(fieldModel.key, { ...component.settings, cssClass, message });
    });

    this.form = new UntypedFormGroup(formGroup, { validators: this.config.getCustomValidator() });
    this.subscribers.formChanges = this.form.valueChanges.subscribe(() => {
      this.dynamicList?.forEach((component: ControlDynamicComponent<any>) => {
        component?.componentRef?.changeDetectorRef?.markForCheck();
      });
    });
  }

  initSubs(): void {
    const { id, repository } = this.data;
    if (!id) {
      /*
      only for create modal
       */
      this.config.formHandlerOnInit(this.form);
      setFirstSelectValueByDefault([...this.fields, this.toggleField]);
      this.cd.markForCheck();
      return;
    }
    this.isLoading = true;

    this.subscribers.procress = this.actionsService
      .data(this.config, id, repository)
      .pipe(
        pluck('data'),
        filter((data: Data) => !!data)
      )
      .subscribe((data) => {
        this.dataResponse = data;
        setFormData([...this.fields, this.toggleField], data);
        this.isLoading = false;
        this.config.formHandlerOnInit(this.form);
        if (this.form.invalid) {
          Object.values(this.form.controls).forEach((formControl: UntypedFormControl) => {
            formControl.updateValueAndValidity();
          });
        }
        this.cd.markForCheck();
      });
  }

  getFieldWidth(field: number): string {
    return `col-sm-${this.config.getGrid()[field % this.config.getGrid().length]}`;
  }

  getFieldTooltip(field): string {
    return this.config.getTooltipsFields()[field];
  }

  submit(): void {
    this.config.formHandlerOnSubmit(this.form);
    this.isLoading = true;

    const { id, repository } = this.data;
    let body = this.form.getRawValue();
    if (this.toggleField) {
      body[this.toggleField.key] = this.toggleField.formControl.value;
    }
    body = mapToBackendFormat(body);

    if (body?.email) {
      body.email = (body.email + '').toLowerCase();
    }

    const key = this.config.getKey();
    const configRepository = this.config.getData();
    const { user_has_payment_methods } = this.authService.userData();

    // TODO: refactor
    if (
      key === 'AffiliateCrm\\AffiliatePaymentMethod\\InsertEdit' &&
      configRepository === 'Eloquent\\AffiliatePaymentMethodRepository' &&
      !user_has_payment_methods
    ) {
      this.subscribers.processAndUser = forkJoin([
        this.actionsService.process(this.config, body, id, repository),
        this.authService.getUser(),
      ])
        .pipe(filter(([process]) => !!process))
        .subscribe(
          ([process, user]) => {
            this.authService.setUser(user);
            this.close();
            this.pageService.refresh();
            this.isLoading = false;
            const more = process?.data?.messages || [];
            this.callback();

            this.notificationsService.add(
              {
                type: 'success',
                title: $localize`:@@common.success:Success`,
                more,
              },
              true
            );
          },
          (error) => {
            this.isLoading = false;
            Object.entries(error?.errors || {}).forEach(([key, value]) => this.form.get(key)?.setErrors({ customValidate: value[0] }));
          }
        );

      return;
    }

    this.subscribers.process = this.actionsService
      .process(this.config, body, id, repository)
      .pipe(filter((data) => !!data))
      .subscribe(
        (data: any) => {
          this.close();
          this.pageService.refresh();
          this.isLoading = false;
          const more = data?.data?.messages || [];

          this.notificationsService.add(
            {
              type: 'success',
              title: $localize`:@@common.success:Success`,
              more,
            },
            true
          );
        },
        (error) => {
          this.isLoading = false;
          Object.entries(error?.errors || {}).forEach(([key, value]) => this.form.get(key)?.setErrors({ customValidate: value[0] }));
        }
      );
  }

  ngOnDestroy() {
    this.form.reset();
  }

  trackByFn(index: number): number {
    return index;
  }
}

interface ActionFormData {
  config: () => ActionConfigInterface;
  id: number;
  repository: any;
}
